// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
const Barcode = require("react-barcode");

export interface BarcodeItemStylesProps {
    wrapper?: InjectableCss;
    description?: TypographyPresentationProps;
    codeWrapper?: InjectableCss;
    lineInfoWrapper?: InjectableCss;
    lineCardFooter?: InjectableCss;
}

export const barcodeItemStyles: BarcodeItemStylesProps = {
    wrapper: {
        css: css`
            width: 50%;
            padding: 10px;
            display: inline-flex;
            position: relative;
            flex-direction: column;
            align-items: center;
            height: 300px;
            overflow: hidden;
            break-inside: avoid-page;
            -webkit-break-inside: avoid;
            page-break-inside: avoid;
        `,
    },
    description: {
        css: css`
            display: block;
            width: 90%;
            height: 45px;
            overflow: hidden;
            text-align: center;
            white-space: pre-wrap;
        `,
    },
    lineInfoWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 90%;
        `,
    },
    lineCardFooter: {
        css: css`
            text-align: right;
            width: 90%;
            display: inline-flex;
            justify-content: flex-end;
            align-items: center;
        `,
    },
};

interface Props {
    cartLines?: CartLineModel[] | null;
    msgs: any;
}

type OwnProps = Props;

const BarcodeItems: FC<OwnProps> = ({ cartLines, msgs }) => {
    return (
        <>
            {cartLines?.map(line => (
                <StyledWrapper key={line.id} {...barcodeItemStyles.wrapper}>
                    <Typography {...barcodeItemStyles.description}>{line.shortDescription}</Typography>
                    <StyledWrapper {...barcodeItemStyles.codeWrapper}>
                        <Barcode
                            width={1.5}
                            height={80}
                            displayValue={false}
                            key={line.id}
                            value={`${line.erpNumber}@${line.qtyOrdered}`}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...barcodeItemStyles.lineInfoWrapper}>
                        <StyledWrapper>
                            {line.erpNumber && (
                                <Typography
                                    css={css`
                                        display: block;
                                    `}
                                >
                                    {msgs.yourPart} {line.erpNumber}
                                </Typography>
                            )}
                            {line.properties["specNumber"] && (
                                <Typography
                                    css={css`
                                        display: block;
                                    `}
                                >
                                    {msgs.specNumber} {line.properties["specNumber"]}
                                </Typography>
                            )}
                            {line.manufacturerItem && (
                                <Typography
                                    css={css`
                                        display: block;
                                    `}
                                >
                                    {msgs.mfr} {line.manufacturerItem}
                                </Typography>
                            )}
                            {line.customerName && (
                                <Typography
                                    css={css`
                                        display: block;
                                    `}
                                >
                                    {msgs.partNumber} {line.customerName}
                                </Typography>
                            )}
                        </StyledWrapper>
                        <StyledWrapper>
                            <Typography>
                                {translate("Qty:")}{" "}
                                {`${line.qtyOrdered}${line.unitOfMeasure ? `/${line.unitOfMeasure}` : ""}`}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...barcodeItemStyles.lineCardFooter}>
                        <Typography>
                            {translate("www.electrowire.com")}
                        </Typography>
                        <LazyImage
                            src="https://res.cloudinary.com/gcginc/ew-telecom-broadband/logo.svg"
                            css={css`
                                margin-left: 5px;
                            `}
                            altText="Logo"
                            height="30px"
                            width="30px"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </>
    );
};

export default BarcodeItems;
